/**
 * Update Notification Composable
 *
 * Manages update notification state for PWA updates.
 * Handles major vs minor update detection and user dismiss preferences.
 *
 * @module useUpdateNotification
 */

import { ref, computed } from 'vue';
import { compareVersions } from 'src/utils/cacheRecovery';
import { useLogger } from 'src/utils/logger';

const logger = useLogger('UpdateNotification');

// Storage keys
const STORAGE_KEY_DISMISSED = 'pwa-update-dismissed';
const STORAGE_KEY_DISMISSED_VERSION = 'pwa-update-dismissed-version';

// Module-level refs for singleton pattern (shared state across all component instances)
const updateAvailable = ref(false);
const currentVersion = ref('');
const newVersion = ref('');
const registration = ref<ServiceWorkerRegistration | null>(null);
const isDismissed = ref(false);

/**
 * Parse major version number from semver string
 * @param version - Semantic version string (e.g., "1.2.3")
 * @returns Major version number
 */
function parseMajorVersion(version: string): number {
  if (!version) return 0;
  const parts = version.split('.');
  return parseInt(parts[0], 10) || 0;
}

/**
 * Fetch new version from server
 * @returns Promise resolving to version string
 */
async function fetchNewVersion(): Promise<string> {
  try {
    const response = await fetch('/version.txt', {
      cache: 'no-store',
      headers: {
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache',
      },
    });

    if (!response.ok) {
      logger.warn('Failed to fetch version.txt:', response.status);
      return '';
    }

    const text = await response.text();
    return text.trim();
  } catch (error) {
    logger.error('Error fetching version:', error);
    return '';
  }
}

/**
 * Check if the given version was already dismissed in this session
 * @param version - Version to check
 * @returns True if this version was dismissed
 */
function checkDismissed(version: string): boolean {
  try {
    const dismissed = sessionStorage.getItem(STORAGE_KEY_DISMISSED);
    const dismissedVersion = sessionStorage.getItem(STORAGE_KEY_DISMISSED_VERSION);

    // Was dismissed and the same version
    return dismissed === 'true' && dismissedVersion === version;
  } catch {
    return false;
  }
}

/**
 * Set update available state (module-level function for event handler)
 * Called when service worker detects a new version
 *
 * @param reg - Service worker registration
 * @param version - New version string
 */
function setUpdateAvailable(
  reg: ServiceWorkerRegistration,
  version: string
): void {
  logger.info('Update available', {
    currentVersion: currentVersion.value,
    newVersion: version,
  });

  registration.value = reg;
  newVersion.value = version;
  updateAvailable.value = true;

  // Check if this version was already dismissed
  if (checkDismissed(version)) {
    isDismissed.value = true;
    logger.debug('Update was previously dismissed for this version');
  } else {
    isDismissed.value = false;
  }
}

/**
 * Initialize composable state
 * Sets current version and checks for previously dismissed state
 */
function initialize(): void {
  // Set current version from environment
  currentVersion.value = process.env.APP_VUE_CURRENT_VERSION || '';

  logger.debug('Initializing update notification', {
    currentVersion: currentVersion.value,
  });

  // Check if there's a previously set newVersion that was dismissed
  if (newVersion.value && checkDismissed(newVersion.value)) {
    isDismissed.value = true;
  }
}

// Initialize on module load
initialize();

// Listen for custom event from service worker registration
if (typeof window !== 'undefined') {
  window.addEventListener('pwa-update-available', ((event: CustomEvent<{
    registration: ServiceWorkerRegistration;
    newVersion: string;
  }>) => {
    const { registration: reg, newVersion: version } = event.detail;
    setUpdateAvailable(reg, version);
  }) as EventListener);
}

/**
 * Update Notification Composable
 *
 * Provides reactive state and methods for managing PWA update notifications.
 *
 * @example
 * ```typescript
 * const {
 *   updateAvailable,
 *   shouldShowBanner,
 *   shouldShowModal,
 *   applyUpdate,
 *   dismiss,
 * } = useUpdateNotification();
 * ```
 */
export function useUpdateNotification() {
  /**
   * Computed: Is this a major version update?
   * Compares major version numbers (first segment of semver)
   */
  const isMajorUpdate = computed(() => {
    if (!currentVersion.value || !newVersion.value) return false;

    const currentMajor = parseMajorVersion(currentVersion.value);
    const newMajor = parseMajorVersion(newVersion.value);

    return newMajor > currentMajor;
  });

  /**
   * Computed: Should show the dismissible banner?
   * True for minor/patch updates that haven't been dismissed
   */
  const shouldShowBanner = computed(() => {
    return updateAvailable.value && !isDismissed.value && !isMajorUpdate.value;
  });

  /**
   * Computed: Should show the mandatory modal?
   * True for major version updates
   */
  const shouldShowModal = computed(() => {
    return updateAvailable.value && isMajorUpdate.value;
  });

  /**
   * Apply the pending update
   * Signals the waiting service worker to activate and reloads the page
   */
  function applyUpdate(): void {
    logger.info('Applying update...');

    if (!registration.value?.waiting) {
      logger.warn('No waiting service worker to activate');
      // Still reload to ensure latest version
      window.location.reload();
      return;
    }

    // Listen for controller change BEFORE posting skip_waiting message
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      logger.info('Controller changed, reloading...');
      window.location.reload();
    }, { once: true });

    // Signal the waiting service worker to activate
    registration.value.waiting.postMessage({ type: 'SKIP_WAITING' });

    // Fallback reload in case controllerchange doesn't fire
    setTimeout(() => {
      logger.warn('Controllerchange timeout, forcing reload');
      window.location.reload();
    }, 3000);
  }

  /**
   * Dismiss the update notification
   * Stores dismiss state in sessionStorage (persists for current session)
   */
  function dismiss(): void {
    logger.info('Update notification dismissed', {
      version: newVersion.value,
    });

    isDismissed.value = true;

    try {
      sessionStorage.setItem(STORAGE_KEY_DISMISSED, 'true');
      sessionStorage.setItem(STORAGE_KEY_DISMISSED_VERSION, newVersion.value);
    } catch (error) {
      logger.warn('Failed to persist dismiss state:', error);
    }
  }

  return {
    // State
    updateAvailable,
    currentVersion,
    newVersion,
    registration,
    isDismissed,

    // Computed
    isMajorUpdate,
    shouldShowBanner,
    shouldShowModal,

    // Methods
    setUpdateAvailable,
    applyUpdate,
    dismiss,
    fetchNewVersion,
    checkDismissed,
  };
}

// Export types
export interface UpdateNotificationState {
  updateAvailable: typeof updateAvailable;
  currentVersion: typeof currentVersion;
  newVersion: typeof newVersion;
  registration: typeof registration;
  isDismissed: typeof isDismissed;
}
